import { useState } from 'react';
import { createPortal } from 'react-dom';
import VTOLoginConfirmationPortal from '../virtual-try-on/VTOLoginConfirmationPortal';
import './ShareLoginPrompt.css';

/**
 * Modal shown to unauthenticated users when they open a share link.
 * Prompts them to log in to claim the shared items.
 */
const ShareLoginPrompt = ({ isOpen, onDismiss, shareInfo }) => {
  const [showLoginPortal, setShowLoginPortal] = useState(false);

  if (!isOpen) return null;

  // Prefer the person's name over their handle -- "persona_p10 shared an item
  // with you" reads as a system account, not a person.
  const sharerName = shareInfo?.sharer?.displayName || shareInfo?.sharer?.username || 'A friend';
  // Looks lead the preview — a finished outfit reads better than a flat garment
  const items = [...(shareInfo?.looks || []), ...(shareInfo?.clothingItems || [])];
  const bundleTitle = shareInfo?.title;
  const totalCount = items.length;

  const handleLogin = () => {
    setShowLoginPortal(true);
  };

  const handleLoginSuccess = () => {
    setShowLoginPortal(false);
    // Don't reload — Redux isAuthenticated change triggers the claim effect
    // in useShareFlow, which will claim the items and show the success modal.
    // A reload would abort the async claim before it completes.
  };

  const handleLoginClose = () => {
    setShowLoginPortal(false);
  };

  return createPortal(
    <>
      <div className="share-login-overlay" onClick={(e) => { e.stopPropagation(); onDismiss(); }}>
        <div className="share-login-modal" onClick={e => e.stopPropagation()}>
          {/* Close button */}
          <button className="share-login-close" onClick={onDismiss} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4.5 4.5l9 9M13.5 4.5l-9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>

          {/* Icon */}
          <div className="share-login-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 12V22H4V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 7H2V12H22V7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 22V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 7H7.5C6.83696 7 6.20107 6.73661 5.73223 6.26777C5.26339 5.79893 5 5.16304 5 4.5C5 3.83696 5.26339 3.20107 5.73223 2.73223C6.20107 2.26339 6.83696 2 7.5 2C11 2 12 7 12 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 7H16.5C17.163 7 17.7989 6.73661 18.2678 6.26777C18.7366 5.79893 19 5.16304 19 4.5C19 3.83696 18.7366 3.20107 18.2678 2.73223C17.7989 2.26339 17.163 2 16.5 2C13 2 12 7 12 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <div className="share-login-title">
            {sharerName} shared{' '}
            {totalCount > 1 ? `${totalCount} items` : totalCount === 1 ? 'an item' : 'their closet'}{' '}
            with you!
          </div>
          <div className="share-login-subtitle">
            {bundleTitle ? `From \u201C${bundleTitle}\u201D \u00B7 ` : ''}
            Log in to add {totalCount > 1 ? 'them' : 'it'} to your closet
          </div>

          {/* Item previews */}
          {items.length > 0 && (
            <div className="share-login-items">
              {items.slice(0, 8).map((item, idx) => (
                <div key={item.id || idx} className="share-login-item">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.clothesName || 'Shared item'} />
                  ) : (
                    <div className="share-login-item-placeholder">👕</div>
                  )}
                </div>
              ))}
              {items.length > 8 && (
                <div className="share-login-item share-login-item-more">+{items.length - 8}</div>
              )}
            </div>
          )}

          <button className="share-login-btn" onClick={handleLogin}>
            Log in to claim
          </button>
          <button className="share-login-dismiss" onClick={onDismiss}>
            Maybe later
          </button>
        </div>
      </div>

      {/* Login modal portal */}
      <VTOLoginConfirmationPortal
        isOpen={showLoginPortal}
        onLogin={handleLoginSuccess}
        onCancel={handleLoginClose}
        onClose={handleLoginClose}
      />
    </>,
    document.body
  );
};

export default ShareLoginPrompt;
